// CoVerify pricing snapshot on /coverify, consumes shared PRICING_PRODUCTS so
// values stay in lockstep with /pricing. Compact card layout + live calculator.
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Calculator, ArrowRight, ArrowUpRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { findProduct } from "@/constants/pricingCatalog";

function activeTierFor(credits, tiers) {
  return tiers.find((t) => credits >= t.min && (t.max == null || credits <= t.max)) || tiers[0];
}
function perCreditPrice(tier, base) {
  return base * (1 - (tier.discountPct || 0) / 100);
}

export default function Pricing() {
  const product = findProduct("coverify");
  const cfg = product.calculator;
  const [credits, setCredits] = useState(cfg.defaultCredits);

  const safe = Math.max(cfg.minCredits, Math.floor(Number(credits) || cfg.minCredits));
  const activeTier = activeTierFor(safe, cfg.tiers);
  const perCredit = perCreditPrice(activeTier, cfg.basePricePerCredit);
  const total = perCredit * safe;
  const isCustom = !!activeTier.priceLabel;

  return (
    <section id="pricing" className="py-20 md:py-28 bg-white border-y border-border">
      <div className="container-cv">
        <div className="max-w-3xl">
          <span className="section-eyebrow">CoVerify pricing</span>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-bold text-primary tracking-tight leading-[1.08]">
            Simple, transparent{" "}
            <span className="text-accent">verification tokens.</span>
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground">
            Buy CoVerify tokens and use them anywhere across the suite. Base price <b>£{cfg.basePricePerCredit.toFixed(2)}</b> per token, with automatic volume discounts at higher tiers.
          </p>
        </div>

        <div className="mt-12 grid lg:grid-cols-12 gap-6 items-start">
          {/* Tier cards */}
          <div className="lg:col-span-7 grid sm:grid-cols-3 gap-4">
            {cfg.tiers.map((t) => {
              const isActive = t.key === activeTier.key;
              const displayPrice = t.priceLabel
                ? t.priceLabel
                : `£${(cfg.basePricePerCredit * (1 - (t.discountPct || 0) / 100)).toFixed(2)}`;
              return (
                <div
                  key={t.key}
                  data-testid={`coverify-pricing-tier-${t.key}`}
                  className={`relative rounded-2xl p-6 flex flex-col transition-all ${
                    isActive
                      ? "bg-primary text-primary-foreground border-2 border-accent shadow-[0_24px_60px_-24px_rgba(14,58,95,0.35)]"
                      : "bg-white border border-border hover:border-accent/60 hover:-translate-y-0.5"
                  }`}
                >
                  {t.badge && (
                    <span className={`absolute -top-3 left-6 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.14em] rounded-full px-2.5 py-1 ${
                      isActive ? "bg-accent text-primary" : "bg-accent/15 text-primary"
                    }`}>
                      {t.badge.includes("off") || t.badge.includes("Custom") ? <Sparkles className="h-3 w-3" /> : null}
                      {t.badge}
                    </span>
                  )}
                  <div className={`text-[10px] font-bold uppercase tracking-[0.14em] ${isActive ? "text-accent" : "text-muted-foreground"}`}>
                    {t.max == null ? `${t.min}+ tokens` : `${t.min} – ${t.max} tokens`}
                  </div>
                  <div className={`mt-1 font-display text-2xl font-bold tracking-tight ${isActive ? "" : "text-primary"}`}>{t.name}</div>

                  <div className="mt-4">
                    <div className={`font-display text-3xl font-black tracking-tight ${isActive ? "" : "text-primary"}`}>
                      {displayPrice}
                    </div>
                    <div className={`mt-1 text-xs ${isActive ? "text-white/70" : "text-muted-foreground"}`}>
                      {t.priceLabel ? "custom-quoted" : "/ token"}
                      {t.discountPct ? ` · ${t.discountPct}% off` : ""}
                    </div>
                  </div>

                  <p className={`mt-3 text-xs leading-relaxed ${isActive ? "text-white/75" : "text-muted-foreground"}`}>
                    {t.tagline}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Calculator */}
          <div className="lg:col-span-5 rounded-2xl border border-accent/40 bg-white p-6 md:p-8 shadow-[0_18px_48px_-18px_rgba(14,58,95,0.18)]">
            <div className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.16em] text-primary">
              <Calculator className="h-3.5 w-3.5 text-accent" /> Volume calculator
            </div>
            <h3 className="mt-3 font-display text-2xl font-bold text-primary tracking-tight">How many tokens do you need?</h3>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
              Minimum {cfg.minCredits} tokens. Discount applies automatically.
            </p>

            <label className="mt-5 block text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground">
              Number of tokens
            </label>
            <div className="mt-2 flex items-stretch rounded-xl border border-border overflow-hidden focus-within:border-accent transition-colors">
              <button
                type="button"
                onClick={() => setCredits((c) => Math.max(cfg.minCredits, Math.floor(Number(c) || cfg.minCredits) - 5))}
                className="px-4 text-primary hover:bg-secondary font-bold text-lg"
                aria-label="decrease"
                data-testid="coverify-calc-decr"
              >−</button>
              <input
                type="number"
                min={cfg.minCredits}
                step={1}
                value={credits}
                onChange={(e) => setCredits(e.target.value)}
                onBlur={() => setCredits(safe)}
                data-testid="coverify-calc-input"
                className="flex-1 text-center text-2xl font-display font-black text-primary px-3 py-3 outline-none"
              />
              <button
                type="button"
                onClick={() => setCredits((c) => Math.floor(Number(c) || cfg.minCredits) + 5)}
                className="px-4 text-primary hover:bg-secondary font-bold text-lg"
                aria-label="increase"
                data-testid="coverify-calc-incr"
              >+</button>
            </div>

            <dl className="mt-5 space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-muted-foreground">Active tier</dt><dd className="font-semibold text-primary">{activeTier.name}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Per token</dt><dd className="font-semibold text-primary">{isCustom ? activeTier.priceLabel : `£${perCredit.toFixed(2)}`}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Tokens</dt><dd className="font-semibold text-primary">{safe.toLocaleString()}</dd></div>
            </dl>

            <div className="mt-5 rounded-xl bg-secondary/50 border border-border p-5 flex items-baseline justify-between">
              <span className="text-xs font-bold uppercase tracking-[0.14em] text-primary">Estimated total</span>
              <span className="font-display text-3xl font-black text-primary">
                {isCustom ? activeTier.priceLabel : `£${total.toFixed(2)}`}
              </span>
            </div>

            <Button asChild data-testid="coverify-pricing-cta" className="mt-5 h-12 w-full bg-accent hover:bg-accent/90 text-primary font-bold">
              <Link to={activeTier.href || "/contact?product=coverify"}>
                {activeTier.cta || "Talk to sales"} <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>

            <Link
              to="/pricing#coverify"
              data-testid="coverify-pricing-see-full"
              className="mt-3 inline-flex items-center justify-center gap-1 w-full text-xs font-bold text-primary hover:text-accent transition-colors"
            >
              See full comparison on Pricing page <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>

        {/* Included features summary, pulls bullets from the active tier */}
        <div className="mt-10 rounded-2xl border border-border bg-secondary/30 p-6 md:p-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">What&apos;s in the</div>
              <div className="mt-1 font-display text-xl font-bold text-primary tracking-tight">{activeTier.name} tier</div>
            </div>
            <Link
              to="/pricing#coverify"
              className="inline-flex items-center gap-1.5 text-sm font-bold text-primary hover:text-accent transition-colors"
            >
              Compare all features <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
          <ul className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2.5">
            {activeTier.includes.map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-primary/90">
                <Check className="h-4 w-4 mt-0.5 text-accent shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
